"use client";

import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { getArbitrageLiveClient, getDiscrepanciesLiveClient, getFantasyLiveClient, getLowHoldLiveClient, getOddsLiveClient, getOptimizerLiveClient, getSniperLiveClient, getWhaleLiveClient, type OddsLiveClient } from "@/lib/odds-screen/live/client";
import { INPLAY_EVENT_TOPICS, createSlots, inplayGames, inplayLadder, inplayRows } from "@/lib/odds-screen/live/inplay";
import { emptyTopicState, mergePropsViews } from "@/lib/odds-screen/live/store";
import {
  topicId,
  type ArbitrageTopic, type ArbitrageView, type BoardRow, type BoardTopic, type BoardView, type BookId, type BookRowsTopic, type Catalog, type CatalogTopic, type ConnectionStatus, type Dfs, type DfsTopic, type DiscrepanciesTopic, type DiscrepanciesView, type EventHeader, type EventId, type FamilyId, type InplayEvent, type InplayEventTopic, type InplayGame, type InplayList, type InplayStatus, type InplayTopic, type Ladder, type LadderTopic, type LeagueId, type LowHoldTier, type LowHoldTopic, type LowHoldView, type MarketSpec, type PropsView, type RaceList, type RaceTopic, type RowId, type SniperRowsTopic, type SniperView, type Topic, type TopicState,
  type WhaleDetail, type WhaleList, type WhaleRowTopic, type WhaleTier, type WhaleTopic,
} from "@/lib/odds-screen/live/types";

export type LadderTarget = Omit<LadderTopic, "kind">;

const CATALOG_TOPIC: CatalogTopic = { kind: "catalog" };
const EMPTY_CATALOG: TopicState<Catalog> = emptyTopicState<Catalog>();
const DFS_TOPIC: DfsTopic = { kind: "dfs" };
const EMPTY_DFS: TopicState<Dfs> = emptyTopicState<Dfs>();

export const PACE_MS = 250;

type Listen = (onChange: () => void) => () => void;

export function pacedListen(listen: Listen, stage: () => string, paceMs: number): Listen {
  return (onChange) => {
    let firedAt = 0;
    let firedStage = stage();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const fire = () => {
      timer = null;
      firedAt = Date.now();
      firedStage = stage();
      onChange();
    };
    const stop = listen(() => {
      const wait = firedAt + paceMs - Date.now();
      if (wait <= 0 || stage() !== firedStage) {
        if (timer !== null) clearTimeout(timer);
        fire();
        return;
      }
      if (timer === null) timer = setTimeout(fire, wait);
    });
    return () => {
      stop();
      if (timer !== null) clearTimeout(timer);
    };
  };
}

function viewStage(view: { readonly phase: string; readonly ended: string | null }): string {
  return `${view.phase}|${view.ended ?? ""}`;
}

function useTopic(client: OddsLiveClient, topic: Topic | null): void {
  const id = topic === null ? null : topicId(topic);
  useEffect(() => (topic === null ? undefined : client.retain(topic)), [client, id]);
}

export function useCatalog(client: OddsLiveClient = getOddsLiveClient()): TopicState<Catalog> {
  useTopic(client, CATALOG_TOPIC);
  const listen = useCallback((onChange: () => void) => client.listen(CATALOG_TOPIC, onChange), [client]);
  return useSyncExternalStore(listen, () => client.read(CATALOG_TOPIC), () => EMPTY_CATALOG);
}

export function useDfs(client: OddsLiveClient): TopicState<Dfs> {
  useTopic(client, DFS_TOPIC);
  const listen = useCallback((onChange: () => void) => client.listen(DFS_TOPIC, onChange), [client]);
  return useSyncExternalStore(listen, () => client.read(DFS_TOPIC), () => EMPTY_DFS);
}

export function useBoard(league: LeagueId, family: FamilyId): BoardView {
  const client = getOddsLiveClient();
  const topic = useMemo<BoardTopic>(() => ({ kind: "board", league, family }), [league, family]);
  useTopic(client, topic);
  const listen = useCallback((onChange: () => void) => client.listen(topic, onChange), [client, topic]);
  const read = () => client.readBoardView(topic);
  return useSyncExternalStore(listen, read, read);
}

function useBoardRow(league: LeagueId, family: FamilyId, rowId: RowId): BoardRow | undefined {
  const client = getOddsLiveClient();
  const topic = useMemo<BoardTopic>(() => ({ kind: "board", league, family }), [league, family]);
  const listen = useCallback((onChange: () => void) => client.listen(topic, onChange), [client, topic]);
  const read = () => client.read(topic).value?.rows.get(rowId);
  return useSyncExternalStore(listen, read, read);
}

const NO_LISTEN: Listen = () => () => {};

export function useInplayList(league: LeagueId, enabled: boolean): InplayList | null {
  const client = getOddsLiveClient();
  const topic = useMemo<InplayTopic | null>(() => (enabled ? { kind: "live", league } : null), [enabled, league]);
  useTopic(client, topic);
  const listen = useMemo<Listen>(
    () => (topic === null ? NO_LISTEN : (onChange) => client.listen(topic, onChange)),
    [client, topic],
  );
  const read = () => (topic === null ? null : client.read(topic).value);
  return useSyncExternalStore(listen, read, read);
}

const INPLAY_SLOTS = createSlots(INPLAY_EVENT_TOPICS);

export function useInplayEvent(league: LeagueId, event: EventId | null, family: FamilyId): InplayEvent | null {
  const client = getOddsLiveClient();
  const slot = event === null ? null : `${league}|${event}|${family}`;
  useEffect(() => (slot === null ? undefined : INPLAY_SLOTS.acquire(slot)), [slot]);
  const granted = useSyncExternalStore(INPLAY_SLOTS.listen, () => slot !== null && INPLAY_SLOTS.granted(slot), () => false);
  const topic = useMemo<InplayEventTopic | null>(
    () => (granted && event !== null ? { kind: "live_event", league, event, family } : null),
    [granted, league, event, family],
  );
  useTopic(client, topic);
  const listen = useMemo<Listen>(
    () => (topic === null ? NO_LISTEN : (onChange) => client.listen(topic, onChange)),
    [client, topic],
  );
  const read = () => (topic === null ? null : client.read(topic).value);
  return useSyncExternalStore(listen, read, read);
}

export function useInplayBoardGames(family: FamilyId | null, list: InplayList | null): readonly InplayGame[] {
  return useMemo(() => inplayGames(family === null ? null : list, family ?? ""), [family, list]);
}

export function useRow(league: LeagueId, family: FamilyId, rowId: RowId, event: EventHeader | undefined, specs: readonly MarketSpec[]): BoardRow | undefined {
  const pregame = useBoardRow(league, family, rowId);
  const inplay = event?.live === undefined ? null : event.id;
  const cells = useInplayEvent(league, inplay, family);
  const live = useMemo(() => (inplay === null || cells === null ? undefined : inplayRows(inplay, cells, specs).get(rowId)), [inplay, cells, specs, rowId]);
  return inplay === null ? pregame : live;
}

export type EventRowIds = { readonly loading: boolean; readonly rowIds: readonly RowId[] };

const LOADING_ROWS: EventRowIds = { loading: true, rowIds: [] };

export function useEventRowIds(league: LeagueId, family: FamilyId, event: EventHeader, specs: readonly MarketSpec[]): EventRowIds {
  const view = useBoard(league, family);
  const inplay = event.live === undefined ? null : event.id;
  const cells = useInplayEvent(league, inplay, family);
  return useMemo(() => {
    if (inplay === null) return { loading: view.phase === "loading", rowIds: view.rowIds.filter((rowId) => view.rows.get(rowId)?.eventId === event.id) };
    return cells === null ? LOADING_ROWS : { loading: false, rowIds: [...inplayRows(inplay, cells, specs).keys()] };
  }, [inplay, cells, specs, view, event.id]);
}

export function useGameLadder(target: LadderTarget | null, event: EventHeader | undefined, specs: readonly MarketSpec[]): Ladder | null {
  const inplay = event?.live !== undefined;
  const pregame = useLadder(inplay ? null : target);
  const cells = useInplayEvent(target?.league ?? "", inplay ? target?.event ?? null : null, target?.family ?? "");
  const entity = target?.entity ?? "";
  const live = useMemo(() => (cells === null ? null : inplayLadder(cells, specs, entity)), [cells, specs, entity]);
  return inplay ? live : pregame?.value ?? null;
}

export function usePropsMany(leagues: readonly LeagueId[], book: BookId | null): PropsView {
  return useBookRowsMany(getFantasyLiveClient(), "props", leagues, book, 0);
}

export function useRace(book: BookId | null): TopicState<RaceList> | null {
  const client = getFantasyLiveClient();
  const topic = useMemo<RaceTopic | null>(() => (book === null ? null : { kind: "race", book }), [book]);
  useTopic(client, topic);
  const listen = useCallback(
    (onChange: () => void) => (topic === null ? () => {} : client.listen(topic, onChange)),
    [client, topic],
  );
  const read = () => (topic === null ? null : client.read(topic));
  return useSyncExternalStore(listen, read, read);
}

export function useOptimizerMany(leagues: readonly LeagueId[], book: BookId | null): PropsView {
  return useBookRowsMany(getOptimizerLiveClient(), "optimizer", leagues, book, PACE_MS);
}

function useBookRowsMany(client: OddsLiveClient, kind: BookRowsTopic["kind"], leagues: readonly LeagueId[], book: BookId | null, paceMs: number): PropsView {
  const key = leagues.join("|");
  const topics = useMemo<BookRowsTopic[]>(
    () => (book === null ? [] : leagues.map((league) => ({ kind, league, book }))),
    [key, kind, book], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => {
    const releases = topics.map((topic) => client.retain(topic));
    return () => { for (const release of releases) release(); };
  }, [client, topics]);
  const cache = useRef<{ parts: readonly PropsView[]; merged: PropsView } | null>(null);
  const read = () => {
    const parts = topics.map((topic) => client.readPropsView(topic));
    const held = cache.current;
    if (held !== null && held.parts.length === parts.length && held.parts.every((part, index) => part === parts[index])) return held.merged;
    const merged = mergePropsViews(parts);
    cache.current = { parts, merged };
    return merged;
  };
  const listen = useMemo(() => {
    const raw: Listen = (onChange) => {
      const stops = topics.map((topic) => client.listen(topic, onChange));
      return () => { for (const stop of stops) stop(); };
    };
    return paceMs > 0 ? pacedListen(raw, () => topics.map((topic) => viewStage(client.readPropsView(topic))).join(","), paceMs) : raw;
  }, [client, topics, paceMs]);
  return useSyncExternalStore(listen, read, read);
}

export function useDiscrepanciesMany(leagues: readonly LeagueId[]): DiscrepanciesView {
  const client = getDiscrepanciesLiveClient();
  const key = leagues.join("|");
  const topics = useMemo<DiscrepanciesTopic[]>(
    () => leagues.map((league) => ({ kind: "discrepancies", league })),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => {
    const releases = topics.map((topic) => client.retain(topic));
    return () => { for (const release of releases) release(); };
  }, [client, topics]);
  const cache = useRef<{ parts: readonly DiscrepanciesView[]; merged: DiscrepanciesView } | null>(null);
  const read = () => {
    const parts = topics.map((topic) => client.readDiscrepanciesView(topic));
    const held = cache.current;
    if (held !== null && held.parts.length === parts.length && held.parts.every((part, index) => part === parts[index])) return held.merged;
    const merged = mergePropsViews(parts);
    cache.current = { parts, merged };
    return merged;
  };
  const listen = useMemo(() => pacedListen((onChange) => {
    const stops = topics.map((topic) => client.listen(topic, onChange));
    return () => { for (const stop of stops) stop(); };
  }, () => topics.map((topic) => viewStage(client.readDiscrepanciesView(topic))).join(","), PACE_MS), [client, topics]);
  return useSyncExternalStore(listen, read, read);
}

export function useSniperMany(leagues: readonly LeagueId[], live: boolean): SniperView {
  const client = getSniperLiveClient();
  const kind: SniperRowsTopic["kind"] = live ? "sniper_live" : "sniper";
  const paceMs = live ? 0 : PACE_MS;
  const key = leagues.join("|");
  const topics = useMemo<SniperRowsTopic[]>(() => (key === "" ? [] : key.split("|").map((league) => ({ kind, league }))), [key, kind]);
  useEffect(() => {
    const releases = topics.map((topic) => client.retain(topic));
    return () => { for (const release of releases) release(); };
  }, [client, topics]);
  const cache = useRef<{ parts: readonly SniperView[]; merged: SniperView } | null>(null);
  const read = () => {
    const parts = topics.map((topic) => client.readSniperView(topic));
    const held = cache.current;
    if (held !== null && held.parts.length === parts.length && held.parts.every((part, index) => part === parts[index])) return held.merged;
    const merged = mergePropsViews(parts);
    cache.current = { parts, merged };
    return merged;
  };
  const listen = useMemo(() => {
    const raw: Listen = (onChange) => {
      const stops = topics.map((topic) => client.listen(topic, onChange));
      return () => { for (const stop of stops) stop(); };
    };
    return paceMs > 0 ? pacedListen(raw, () => topics.map((topic) => viewStage(client.readSniperView(topic))).join(","), paceMs) : raw;
  }, [client, topics, paceMs]);
  return useSyncExternalStore(listen, read, read);
}

export function useSniperGameStates(leagues: readonly LeagueId[]): ReadonlyMap<EventId, InplayStatus> {
  const client = getSniperLiveClient();
  const key = leagues.join("|");
  const topics = useMemo<InplayTopic[]>(() => (key === "" ? [] : key.split("|").map((league) => ({ kind: "live", league }))), [key]);
  useEffect(() => {
    const releases = topics.map((topic) => client.retain(topic));
    return () => { for (const release of releases) release(); };
  }, [client, topics]);
  const cache = useRef<{ lists: readonly (InplayList | null)[]; statuses: ReadonlyMap<EventId, InplayStatus> } | null>(null);
  const read = () => {
    const lists = topics.map((topic) => client.read(topic).value);
    const held = cache.current;
    if (held !== null && held.lists.length === lists.length && held.lists.every((list, index) => list === lists[index])) return held.statuses;
    const statuses = new Map<EventId, InplayStatus>();
    for (const list of lists) {
      for (const game of list?.games.values() ?? []) {
        if (game.status !== null) statuses.set(game.id, game.status);
      }
    }
    cache.current = { lists, statuses };
    return statuses;
  };
  const listen = useMemo<Listen>(() => (onChange) => {
    const stops = topics.map((topic) => client.listen(topic, onChange));
    return () => { for (const stop of stops) stop(); };
  }, [client, topics]);
  return useSyncExternalStore(listen, read, read);
}

export function useArbitrageMany(leagues: readonly LeagueId[]): ArbitrageView {
  const client = getArbitrageLiveClient();
  const key = leagues.join("|");
  const topics = useMemo<ArbitrageTopic[]>(
    () => leagues.map((league) => ({ kind: "arbitrage", league })),
    [key],
  );
  useEffect(() => {
    const releases = topics.map((topic) => client.retain(topic));
    return () => { for (const release of releases) release(); };
  }, [client, topics]);
  const cache = useRef<{ parts: readonly ArbitrageView[]; merged: ArbitrageView } | null>(null);
  const read = () => {
    const parts = topics.map((topic) => client.readArbitrageView(topic));
    const held = cache.current;
    if (held !== null && held.parts.length === parts.length && held.parts.every((part, index) => part === parts[index])) return held.merged;
    const merged = mergePropsViews(parts);
    cache.current = { parts, merged };
    return merged;
  };
  const listen = useMemo(() => pacedListen((onChange) => {
    const stops = topics.map((topic) => client.listen(topic, onChange));
    return () => { for (const stop of stops) stop(); };
  }, () => topics.map((topic) => viewStage(client.readArbitrageView(topic))).join(","), PACE_MS), [client, topics]);
  return useSyncExternalStore(listen, read, read);
}

const OTHER_TIER: Record<LowHoldTier, LowHoldTier> = { default: "wide", wide: "default" };
const bridges = new WeakMap<LowHoldView, LowHoldView>();

const showable = (view: LowHoldView) => view.phase !== "loading" && view.ended === null;

function bridged(view: LowHoldView, fallback: LowHoldView | undefined): LowHoldView {
  if (view.phase !== "loading" || fallback === undefined) return view;
  let bridge = bridges.get(fallback);
  if (bridge === undefined) {
    bridge = { ...fallback, phase: "loading", pending: 1 };
    bridges.set(fallback, bridge);
  }
  return bridge;
}

type TierPair = { readonly topic: LowHoldTopic; readonly fallback: LowHoldTopic };

export function useLowHoldMany(leagues: readonly LeagueId[], tier: LowHoldTier): LowHoldView {
  const client = getLowHoldLiveClient();
  const key = leagues.join("|");
  const pairs = useMemo<TierPair[]>(
    () => leagues.map((league) => ({ topic: { kind: "lowhold", league, tier }, fallback: { kind: "lowhold", league, tier: OTHER_TIER[tier] } })),
    [key, tier],
  );
  useEffect(() => {
    const releases = pairs.map(({ topic }) => client.retain(topic));
    return () => { for (const release of releases) release(); };
  }, [client, pairs]);
  const remembered = useRef(new Map<LeagueId, LowHoldView>());
  const shown = useMemo(() => {
    const kept = new Map<LeagueId, LowHoldView>();
    for (const league of leagues) {
      const view = remembered.current.get(league);
      if (view !== undefined) kept.set(league, view);
    }
    remembered.current = kept;
    return kept;
  }, [key]);
  const tierView = useCallback(({ topic, fallback }: TierPair) => {
    const view = client.readLowHoldView(topic);
    const latest = [view, client.readLowHoldView(fallback)].find(showable);
    if (latest !== undefined) shown.set(topic.league, latest);
    return bridged(view, shown.get(topic.league));
  }, [client, shown]);
  const cache = useRef<{ parts: readonly LowHoldView[]; merged: LowHoldView } | null>(null);
  const read = () => {
    const parts = pairs.map(tierView);
    const held = cache.current;
    if (held !== null && held.parts.length === parts.length && held.parts.every((part, index) => part === parts[index])) return held.merged;
    const merged = mergePropsViews(parts);
    cache.current = { parts, merged };
    return merged;
  };
  const listen = useMemo(() => pacedListen((onChange) => {
    const stops = pairs.flatMap(({ topic, fallback }) => [client.listen(topic, onChange), client.listen(fallback, onChange)]);
    return () => { for (const stop of stops) stop(); };
  }, () => pairs.map((pair) => viewStage(tierView(pair))).join(","), PACE_MS), [client, pairs, tierView]);
  return useSyncExternalStore(listen, read, read);
}

export function useLadder(target: LadderTarget | null): TopicState<Ladder> | null {
  const client = getOddsLiveClient();
  const league = target?.league;
  const event = target?.event;
  const family = target?.family;
  const entity = target?.entity;
  const topic = useMemo<LadderTopic | null>(
    () => (league === undefined || event === undefined || family === undefined || entity === undefined
      ? null
      : { kind: "ladder", league, event, family, entity }),
    [league, event, family, entity],
  );
  useTopic(client, topic);
  const listen = useCallback(
    (onChange: () => void) => (topic === null ? () => {} : client.listen(topic, onChange)),
    [client, topic],
  );
  const read = () => (topic === null ? null : client.read(topic));
  return useSyncExternalStore(listen, read, read);
}

export function useLadderPrefetch(): (target: LadderTarget) => void {
  const client = getOddsLiveClient();
  return useCallback((target: LadderTarget) => client.prefetch({ kind: "ladder", ...target }), [client]);
}

export function useWhaleList(sport: string, tier: WhaleTier | null): TopicState<WhaleList> | null {
  const client = getWhaleLiveClient();
  const topic = useMemo<WhaleTopic | null>(() => (tier === null ? null : { kind: "whale", sport, tier }), [sport, tier]);
  useTopic(client, topic);
  const listen = useCallback(
    (onChange: () => void) => (topic === null ? () => {} : client.listen(topic, onChange)),
    [client, topic],
  );
  const read = () => (topic === null ? null : client.read(topic));
  return useSyncExternalStore(listen, read, read);
}

export function useWhaleRow(id: RowId | null): TopicState<WhaleDetail> | null {
  const client = getWhaleLiveClient();
  const topic = useMemo<WhaleRowTopic | null>(() => (id === null ? null : { kind: "whale_row", id }), [id]);
  useTopic(client, topic);
  const listen = useCallback(
    (onChange: () => void) => (topic === null ? () => {} : client.listen(topic, onChange)),
    [client, topic],
  );
  const read = () => (topic === null ? null : client.read(topic));
  return useSyncExternalStore(listen, read, read);
}

export function useLiveStatus(client: OddsLiveClient = getOddsLiveClient()): ConnectionStatus {
  return useSyncExternalStore(client.listenStatus, client.status, () => "idle");
}
